import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail } from "lucide-react";
import { useSubscribeMutation } from "../../../stores/api/subscribers.api";

const STORAGE_KEY = "newsletter_dismissed";

const NewsletterModal = () => {
  const [visible, setVisible] = useState(false);
  const [animIn, setAnimIn] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [subscribe, { isLoading }] = useSubscribeMutation();
  const timerRef = useRef(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const onScroll = () => {
      if (scrolledRef.current) return;
      scrolledRef.current = true;
      window.removeEventListener("scroll", onScroll);
      timerRef.current = setTimeout(() => {
        setVisible(true);
        requestAnimationFrame(() => setAnimIn(true));
      }, 3000);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timerRef.current);
    };
  }, []);

  const close = () => {
    setAnimIn(false);
    setTimeout(() => setVisible(false), 300);
    sessionStorage.setItem(STORAGE_KEY, "1");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribe({ email }).unwrap();
      setSubmitted(true);
      setTimeout(close, 2500);
    } catch {
      // silent — user can retry
    }
  };

  if (!visible) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[999] flex items-center justify-center p-4 transition-all duration-300 ${animIn ? "bg-black/50 backdrop-blur-sm" : "bg-black/0"}`}
      onClick={close}
    >
      <div
        className={`relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-300 ${animIn ? "translate-y-0 opacity-100 scale-100" : "translate-y-8 opacity-0 scale-95"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-gray-900 via-gray-600 to-gray-900" />

        <button
          onClick={close}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={16} />
        </button>

        <div className="px-8 py-8">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <Mail size={22} className="text-gray-700" />
          </div>

          {submitted ? (
            <div className="flex flex-col items-center py-4 text-center">
              <div className="relative mb-5 flex h-16 w-16 items-center justify-center">
                <svg viewBox="0 0 64 64" className="h-16 w-16" fill="none">
                  <circle
                    cx="32" cy="32" r="30"
                    stroke="#16a34a" strokeWidth="3"
                    strokeDasharray="188.5"
                    strokeDashoffset="0"
                    className="animate-[stroke-draw_0.5s_ease-out_forwards]"
                    style={{ animation: "stroke-draw 0.5s ease-out forwards", strokeDashoffset: 188.5 }}
                  />
                  <polyline
                    points="18,33 28,43 46,23"
                    stroke="#16a34a" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"
                    strokeDasharray="40" strokeDashoffset="40"
                    style={{ animation: "check-draw 0.4s ease-out 0.4s forwards" }}
                  />
                </svg>
              </div>
              <style>{`
                @keyframes stroke-draw {
                  from { stroke-dashoffset: 188.5; }
                  to   { stroke-dashoffset: 0; }
                }
                @keyframes check-draw {
                  to { stroke-dashoffset: 0; }
                }
              `}</style>
              <p className="text-lg font-semibold text-gray-900" style={{ animation: "fadeUp 0.4s ease-out 0.7s both" }}>You're in!</p>
              <p className="mt-1 text-sm text-gray-500" style={{ animation: "fadeUp 0.4s ease-out 0.85s both" }}>Thanks for subscribing. We'll be in touch soon.</p>
              <style>{`
                @keyframes fadeUp {
                  from { opacity: 0; transform: translateY(6px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
              `}</style>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-gray-900">Stay in the loop</h2>
              <p className="mt-1.5 text-sm text-gray-500">
                Get exclusive deals and new arrivals delivered straight to your inbox.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-700 disabled:opacity-60"
                >
                  {isLoading ? "Subscribing…" : "Subscribe"}
                </button>
              </form>

              <button onClick={close} className="mt-4 w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors">
                No thanks
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NewsletterModal;
